import React, { useEffect, useMemo, useRef } from 'react'

import { Toaster } from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import WalletPortfolioDrawer from '@src/components/WalletPortfolioDrawer'
import PendingTransactionsButton from '@src/components/buttons/PendingTransactionsButton'
import Background from '@src/components/icons/Background'
import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import { useClickOutside } from '@src/hooks/utils/useClickOutside'
import Header from '@src/layouts/Header'
import { useEmbed } from '@src/providers/EmbedProvider'
import { useApplicationStore, useConfigStore } from '@src/zustand_stores'

// Add a type for the window extension
declare global {
    interface Window {
        buttonStyleInterval?: number
    }
}

interface Props {
    children: React.ReactNode
    primaryColor?: string
    backgroundColor?: string
    modalBackgroundColor?: string
    buttonTextColor?: string
    showBranding?: boolean
    logoUrl?: string
    logoHeight?: number
    logoWidth?: number
    logoPosition?: 'left' | 'center' | 'right'
    showPendingTX?: boolean
    pendingTXPosition?: 'left' | 'center' | 'right'
    showHamburgerMenu?: boolean
    useIntuitiveColorDefaults?: boolean
}

/**
 * Computes color variations based on a base color
 * @param baseColor - The base color in hex format (e.g., #123456)
 * @returns Object with computed color variations
 */
const computeColorVariations = (baseColor: string) => {
    // Remove the # from the beginning
    const hex = baseColor.substring(1)

    // Convert hex to RGB
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)

    // Default values in case of parsing errors
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return {
            hoverBackground: 'rgba(255, 255, 255, 0.1)',
            activeBackground: 'rgba(255, 255, 255, 0.15)',
            inputBackground: 'rgba(255, 255, 255, 0.05)',
            inputBorder: 'rgba(255, 255, 255, 0.1)',
            dividerColor: 'rgba(255, 255, 255, 0.1)',
        }
    }

    // Calculate luminance to determine if we should lighten or darken
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // For dark backgrounds (most common case), create lighter variations
    if (luminance < 0.5) {
        // Create slightly lighter variations for hover/active states
        const hoverFactor = 1.2 // 20% lighter
        const activeFactor = 1.3 // 30% lighter

        // Lighten function that respects the 0-255 bounds
        const lighten = (value: number, factor: number) => Math.min(255, Math.round(value * factor))

        // Create hover and active colors by lightening the base
        const hoverR = lighten(r, hoverFactor)
        const hoverG = lighten(g, hoverFactor)
        const hoverB = lighten(b, hoverFactor)

        const activeR = lighten(r, activeFactor)
        const activeG = lighten(g, activeFactor)
        const activeB = lighten(b, activeFactor)

        // Create a slightly lighter color for input backgrounds
        const inputR = lighten(r, 1.1)
        const inputG = lighten(g, 1.1)
        const inputB = lighten(b, 1.1)

        // For dividers, create a color with fixed opacity
        const dividerOpacity = 0.4

        return {
            hoverBackground: `rgb(${hoverR}, ${hoverG}, ${hoverB})`,
            activeBackground: `rgb(${activeR}, ${activeG}, ${activeB})`,
            inputBackground: `rgb(${inputR}, ${inputG}, ${inputB})`,
            inputBorder: `rgba(${r}, ${g}, ${b}, 0.8)`,
            dividerColor: `rgba(255, 255, 255, ${dividerOpacity})`,
        }
    } else {
        // For light backgrounds, create darker variations
        const darkenFactor = 0.8 // 20% darker
        const activeDarkenFactor = 0.7 // 30% darker

        // Darken function that respects the 0-255 bounds
        const darken = (value: number, factor: number) => Math.max(0, Math.round(value * factor))

        // Create hover and active colors by darkening the base
        const hoverR = darken(r, darkenFactor)
        const hoverG = darken(g, darkenFactor)
        const hoverB = darken(b, darkenFactor)

        const activeR = darken(r, activeDarkenFactor)
        const activeG = darken(g, activeDarkenFactor)
        const activeB = darken(b, activeDarkenFactor)

        // Create a slightly darker color for input backgrounds
        const inputR = darken(r, 0.9)
        const inputG = darken(g, 0.9)
        const inputB = darken(b, 0.9)

        // For dividers, create a color with fixed opacity
        const dividerOpacity = 0.2

        return {
            hoverBackground: `rgb(${hoverR}, ${hoverG}, ${hoverB})`,
            activeBackground: `rgb(${activeR}, ${activeG}, ${activeB})`,
            inputBackground: `rgb(${inputR}, ${inputG}, ${inputB})`,
            inputBorder: `rgba(${r}, ${g}, ${b}, 0.8)`,
            dividerColor: `rgba(0, 0, 0, ${dividerOpacity})`,
        }
    }
}

const EmbedLayout: React.FC<Props> = ({
    children,
    primaryColor,
    backgroundColor,
    modalBackgroundColor,
    buttonTextColor = '#FFFFFF',
    showBranding = true,
    logoUrl,
    logoHeight,
    logoWidth,
    logoPosition = 'center',
    showPendingTX = true,
    pendingTXPosition,
    showHamburgerMenu = false,
    useIntuitiveColorDefaults = false,
}) => {
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading, error: tokenLoadingError } = useFetchAllTokens()
    const { walletDrawerOpened, openWalletDrawer } = useApplicationStore()
    const { setIsEmbed, setShowHamburgerMenu, setPrimaryColor } = useEmbed()
    const [searchParams] = useSearchParams()
    const _ref = useClickOutside(() => {
        /* noop */
    })

    // Use a ref to store the observer reference
    const _observerRef = useRef<MutationObserver | null>(null)

    // Set embed state and check for URL parameters
    useEffect(() => {
        // Set isEmbed to true when this component mounts
        setIsEmbed(true)

        // Check for hamburger menu parameter
        const hamburgerMenuParam = searchParams.get('showHamburgerMenu')
        setShowHamburgerMenu(hamburgerMenuParam === 'true' || showHamburgerMenu)

        // Set the primary color
        if (primaryColor) {
            setPrimaryColor(primaryColor)
        }

        return () => {
            // Reset embed state when component unmounts
            setIsEmbed(false)
            setShowHamburgerMenu(false)
            setPrimaryColor(undefined)
        }
    }, [setIsEmbed, setShowHamburgerMenu, setPrimaryColor, primaryColor, showHamburgerMenu, searchParams])

    /**
     * Memoize the container styles to prevent unnecessary re-renders.
     * Since this is a pure calculation that only depends on backgroundColor,
     * we use useMemo to optimize performance when other props change but
     * backgroundColor remains the same, avoiding style recalculations.
     */
    const customStyles = useMemo(() => {
        // Return inline styles for the container
        return {
            backgroundColor: backgroundColor || '#0F1011',
        } as React.CSSProperties
    }, [backgroundColor])

    // Memoize the logo styles for performance
    const logoStyles = useMemo(() => {
        // Construct an object with optional styles
        const styles: React.CSSProperties = {
            objectFit: 'contain',
        }

        // Add height if specified
        if (logoHeight) {
            styles.height = `${logoHeight}px`
        } else {
            styles.height = '40px' // Default height
        }

        // Add width if specified
        if (logoWidth) {
            styles.width = `${logoWidth}px`
        } else {
            styles.maxWidth = '100%' // Default max width
        }

        return styles
    }, [logoHeight, logoWidth])

    /**
     * This effect handles background color customization for the embed widget.
     */
    useEffect(() => {
        // Apply background color if provided
        if (backgroundColor) {
            document.documentElement.style.setProperty('--color-background', backgroundColor)
        }

        // Apply modal background color if provided or default to background color only when useIntuitiveColorDefaults is enabled
        const effectiveModalBgColor = modalBackgroundColor || (useIntuitiveColorDefaults ? backgroundColor : undefined)
        if (effectiveModalBgColor) {
            document.documentElement.style.setProperty('--color-mach-modal-background', effectiveModalBgColor)
            document.documentElement.style.setProperty('--color-token-modal-background', effectiveModalBgColor)

            // Compute color variations based on the modal background
            const colorVariations = computeColorVariations(effectiveModalBgColor)

            // Set CSS variables for the computed colors
            document.documentElement.style.setProperty('--color-hover-background', colorVariations.hoverBackground)
            document.documentElement.style.setProperty('--color-active-background', colorVariations.activeBackground)
            document.documentElement.style.setProperty('--color-input-background', colorVariations.inputBackground)
            document.documentElement.style.setProperty('--color-input-border', colorVariations.inputBorder)
            document.documentElement.style.setProperty('--color-divider', colorVariations.dividerColor)
        }

        // Apply primary color if provided
        if (primaryColor) {
            document.documentElement.style.setProperty('--color-primary', primaryColor)
            document.documentElement.style.setProperty('--color-mach-button-color', primaryColor)

            // Set data attribute for embed mode to trigger the CSS rules
            document.body.setAttribute('data-embed', 'true')
        }

        // Apply button text color
        if (buttonTextColor) {
            document.documentElement.style.setProperty('--color-button-text', buttonTextColor)
        }

        // Cleanup function that runs when the component unmounts
        return () => {
            // Reset CSS variables to their defaults
            document.documentElement.style.removeProperty('--color-background')
            document.documentElement.style.removeProperty('--color-mach-modal-background')
            document.documentElement.style.removeProperty('--color-token-modal-background')
            document.documentElement.style.removeProperty('--color-hover-background')
            document.documentElement.style.removeProperty('--color-active-background')
            document.documentElement.style.removeProperty('--color-input-background')
            document.documentElement.style.removeProperty('--color-input-border')
            document.documentElement.style.removeProperty('--color-divider')
            document.documentElement.style.removeProperty('--color-primary')
            document.documentElement.style.removeProperty('--color-mach-button-color')
            document.documentElement.style.removeProperty('--color-button-text')

            // Remove the data attribute
            document.body.removeAttribute('data-embed')
        }
    }, [backgroundColor, modalBackgroundColor, primaryColor, buttonTextColor, useIntuitiveColorDefaults])

    return (
        <div style={customStyles} className='min-h-screen overflow-auto'>
            <div className='absolute left-0 top-0 -z-10 h-screen w-screen overflow-hidden'>
                <div className='relative h-full w-full'>
                    <Background className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' />
                </div>
            </div>

            <main className='flex min-h-screen flex-grow flex-col items-center justify-start px-2 pb-4 pt-4 font-geist tracking-tightest'>
                {/* Add header space for the hamburger menu */}
                {(showHamburgerMenu || searchParams.get('showHamburgerMenu') === 'true') && (
                    <div className='h-8 w-full'></div>
                )}

                {/* Use Header component with embed mode */}
                {(showHamburgerMenu || searchParams.get('showHamburgerMenu') === 'true') && (
                    <Header isEmbedMode={true} logoUrl={logoUrl} />
                )}

                {/* Simple flexbox header container */}
                <div className='mb-4 flex w-full max-w-[520px] items-center'>
                    {/* Left side */}
                    <div className='flex flex-1 items-center justify-start gap-4'>
                        {logoPosition === 'left' && logoUrl && (
                            <img src={logoUrl} alt='Partner Logo' style={logoStyles} />
                        )}
                        {(pendingTXPosition === 'left' || (!pendingTXPosition && logoPosition === 'right')) &&
                            showPendingTX &&
                            !(showHamburgerMenu || searchParams.get('showHamburgerMenu') === 'true') && (
                                <PendingTransactionsButton className='z-10 shadow-lg' />
                            )}
                    </div>

                    {/* Center */}
                    <div className='flex flex-1 items-center justify-center gap-4'>
                        {logoPosition === 'center' && logoUrl && (
                            <img src={logoUrl} alt='Partner Logo' style={logoStyles} />
                        )}
                        {pendingTXPosition === 'center' &&
                            showPendingTX &&
                            !(showHamburgerMenu || searchParams.get('showHamburgerMenu') === 'true') && (
                                <PendingTransactionsButton className='z-10 shadow-lg' />
                            )}
                    </div>

                    {/* Right side */}
                    <div className='flex flex-1 items-center justify-end gap-4'>
                        {logoPosition === 'right' && logoUrl && (
                            <img src={logoUrl} alt='Partner Logo' style={logoStyles} />
                        )}
                        {(pendingTXPosition === 'right' ||
                            (!pendingTXPosition && (logoPosition === 'left' || logoPosition === 'center'))) &&
                            showPendingTX &&
                            !(showHamburgerMenu || searchParams.get('showHamburgerMenu') === 'true') && (
                                <PendingTransactionsButton className='z-10 shadow-lg' />
                            )}
                    </div>
                </div>

                {((): JSX.Element => {
                    if (configLoading === ConfigLoadingStatus.loading || zustandTokensLoading) {
                        return <>{children}</>
                    } else if (configLoading === ConfigLoadingStatus.error) {
                        console.error(`FATAL: Unable to load config from server.`)
                        return (
                            <div className='exchange-error-message my-auto text-center text-xl font-medium leading-8 text-red-700'>
                                Our trading system is currently down for maintenance. Please try again soon. [1]
                            </div>
                        )
                    } else if (tokenLoadingError) {
                        console.error(`FATAL: Unable to load list of supported tokens from server.`)
                        return (
                            <div className='exchange-error-message my-auto text-center text-xl font-medium leading-8 text-red-700'>
                                Our trading system is currently down for maintenance. Please try again soon. [2]
                            </div>
                        )
                    }
                    return <>{children}</>
                })()}

                {/* Footer branding if enabled */}
                {showBranding && (
                    <div className='mt-4 text-center text-xs text-gray-400'>
                        Powered by{' '}
                        <a
                            href='https://app.mach.exchange'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:underline'
                        >
                            Mach.Exchange
                        </a>
                    </div>
                )}
            </main>

            <WalletPortfolioDrawer open={walletDrawerOpened} onClose={() => openWalletDrawer(false)} />

            <Toaster position='top-center' />
            <ToastContainer position='top-center' />
        </div>
    )
}

export default EmbedLayout
